import { Component, OnDestroy, OnInit, TemplateRef, ViewEncapsulation, ViewChild, ElementRef } from "@angular/core";
import { Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { ApiResources, UtilHelper } from "@shared/helpers";
import { IUserAccount, Page, Pagination, IResource, ProgressData } from "@shared/models";
import { AppData, HttpService, NotifyService, ResourceService } from "@shared/services";
import { finalize, takeUntil } from "rxjs/operators";
import { PackageModuleModel, PackageDocument } from "./models";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { WhiteSpaceValidator } from "../../../../../shared/validators";
import { HttpErrorResponse } from "@angular/common/http";
import { DomSanitizer } from '@angular/platform-browser';
import Swal from "sweetalert2";
import { el } from "@fullcalendar/core/internal-common";

enum ModalType {
    View
}

class TrackBy {
    package(_: number, item: PackageModuleModel): number {
        return item.packageModuleId;
    }
}

class FilterOptions {
    locationId: number = null;
    packageName: string = null;
    packageType: string = null;
    moduleType: string = null;
}

class Filters {
    hidden: boolean;
    options: FilterOptions;
    applied: boolean;

    constructor() {
        this.init();
        this.hidden = true;
    }

    init() {
        this.options = new FilterOptions();
        this.applied = false;
    }
}

@Component({
    templateUrl: "./package-modules.html",
    encapsulation: ViewEncapsulation.None
})
export class PackageModulesPage implements OnInit, OnDestroy {
    page: Page;
    trackBy: TrackBy;
    filters: Filters;
    pagination: Pagination;
    packages: Array<PackageModuleModel>;

    selectedPackage: PackageModuleModel;
    loadingLocations: boolean;
    locations: Array<IResource>;
    loadingPackageTypes: boolean;
    packageTypes: Array<IResource>;
    loadingModuleTypes: boolean;
    moduleTypes: Array<IResource>;

    private modalRef: NgbModalRef;

    uploadDocumentForm: FormGroup;
    maxFiles = 10;
    files: Array<File>;
    submitting: boolean;
    submitted: boolean;
    errorMessage: string;
    packageModuleId: number;

    documents: Array<PackageDocument>;
    document: PackageDocument;
    loadingDocument: boolean;
    documentError: boolean;
    showPrevious: boolean;
    showNext: boolean;
    @ViewChild("videoPlayer", { static: false }) videoPlayer: ElementRef;

    consentForms: Array<IResource>;
    loadingConsentForms: boolean;
    consentFormIds: Array<IResource>;
    updattingConsentIds: boolean;
    updatedConsentIds: boolean;
    consentForm: FormGroup;
    constructor(
        private readonly router: Router,
        private readonly appData: AppData,
        private readonly modalService: NgbModal,
        private readonly httpService: HttpService,
        private readonly notifyService: NotifyService,
        private readonly resourceService: ResourceService,
        private readonly formBuilder: FormBuilder,
        private readonly sanitizer: DomSanitizer
    ) {
        this.page = new Page();
        this.packages = new Array();
        this.filters = new Filters();
        this.trackBy = new TrackBy();
        this.initPagination();
        this.document = new PackageDocument();
        this.selectedPackage = new PackageModuleModel ();
    }

    ngOnInit(): void {
        this.appData.userAccount
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((userAccount: IUserAccount) => {
                if (userAccount) {
                    this.page.userAccount = userAccount;
                    this.fetchPackages();
                    this.fetchLocations();
                    this.fetchPackageTypes();
                    this.fetchModuleTypes();
                    this.buildConsentFomr();
                } else {
                    this.page.userAccount = undefined;
                }
            });
    }

    onApplyFilters(): void {
        this.filters.applied = UtilHelper.isFiltersApplied(this.filters.options);
        this.initPagination();
        this.fetchPackages();
        this.onCloseModal();
    }

    onResetFilters(): void {
        this.filters.init();
        this.filters.options.locationId = this.page.userAccount.locationId;
        this.initPagination();
        this.fetchPackages();
        this.onCloseModal();
    }

    onChangePageSize(pageSize: number): void {
        if (pageSize === this.pagination.pageSize) return;
        this.initPagination();
        this.fetchPackages();
    }

    onNextPage(): void {
        this.fetchPackages();
    }

    onAddPackage(): void {
        this.router.navigateByUrl("/app/masters/add-package-module");
    }

    onEditPackage(packageModuleId: string): void {
        this.router.navigate(["/app/masters/modify-package-module", packageModuleId]);
    }

    onOpenModal(content: TemplateRef<any>, type?: ModalType, model?: PackageModuleModel): void {
        switch (type) {
            case ModalType.View:
                this.selectedPackage = model;
                break;
        }

        this.modalRef = this.modalService.open(content, { size: "lg", windowClass: "custom-modal effect-scale" });
        this.fetchConsentForms();
    }

    onCloseModal(): void {
        if (this.modalRef) {
            this.modalRef.close();
            this.modalRef = undefined;
        }

        this.submitted = undefined;
        this.submitting = undefined;
        this.document = undefined;
        this.loadingDocument = undefined;
        this.documentError = undefined;
        this.showNext = undefined;
        this.showPrevious = undefined;
    }

    onModifyStatus(packageModule: PackageModuleModel, status: boolean) {
        if (packageModule.isModifying) return;
        this.selectedPackage = packageModule;
        let url = ApiResources.getURI(ApiResources.packageModule.base, status ? ApiResources.packageModule.enable : ApiResources.packageModule.disable)
        this.notifyService.confirm(`Are you sure to ${status ? 'enable' : 'disable'} the selected package module?`, () => {
            packageModule.isModifying = true;
            this.httpService
                .post(url, { packageModuleId: this.selectedPackage.packageModuleId, PackageName: this.selectedPackage.packageName, counselingId: this.selectedPackage.packageModuleId, disabledBy: this.page.userAccount.accountId, createdBy: this.page.userAccount.accountId, createdByName: this.page.userAccount.fullName, locationId: this.page.userAccount.locationId, loginRoleId: this.page.userAccount.roleId })
                .pipe(takeUntil(this.page.unSubscribe))
                .pipe(finalize(() => { packageModule.isModifying = false; this.selectedPackage = undefined }))
                .subscribe(
                    (message: string) => {
                        this.notifyService.successToast(message);
                        this.fetchPackages();
                    }
                );
        }, () => { packageModule.isModifying = false;  });
    }

    onDelete(packageModule: PackageModuleModel) {
        if (packageModule.isDeleting) return;
        this.selectedPackage = packageModule;
        this.notifyService.delete(() => {
            packageModule.isDeleting = true;
            this.httpService
                .post(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.delete), { packageModuleId: this.selectedPackage.packageModuleId, PackageName: this.selectedPackage.packageName, counselingId: this.selectedPackage.packageModuleId, disabledBy: this.page.userAccount.accountId, createdBy: this.page.userAccount.accountId, createdByName: this.page.userAccount.fullName, locationId: this.page.userAccount.locationId, loginRoleId: this.page.userAccount.roleId })
                .pipe(takeUntil(this.page.unSubscribe))
                .pipe(finalize(() => { packageModule.isDeleting = false;  }))
                .subscribe(
                    (message: string) => {
                        this.notifyService.successToast(message);
                        this.fetchPackages();
                    }
                );
        }, () => { packageModule.isDeleting = false;  });
    }

    ngOnDestroy(): void {
        this.onCloseModal();
        this.page.unsubscribeAll();
        Swal.close();
    }

    private initPagination(): void {
        this.pagination = new Pagination();
        this.pagination.pageIndex = 1;
        this.pagination.pageSize = 10;
    }

    private createRequestBody(): any {
        const requestBody = Object.assign(UtilHelper.clone(this.filters.options), this.pagination);
        return requestBody;
    }

    private fetchPackages(): void {
        this.page.loading = true;
        this.httpService
            .post(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetch), this.createRequestBody())
            .pipe(
                finalize(() => { this.page.loading = false, this.page.loaded = true }),
                takeUntil(this.page.unSubscribe)
            )
            .subscribe({
                next: (response: Array<PackageModuleModel>) => {
                    this.packages = response;
                    UtilHelper.applyPagination(this.packages, this.pagination);
                },
                error: error => {
                    this.page.markError();
                    const errorMessage = UtilHelper.handleError(error);
                    if (errorMessage)
                        this.notifyService.error(errorMessage);
                    else
                        this.notifyService.defaultError();
                }
            });
    }

    private fetchLocations(): void {
        this.loadingLocations = true;
        this.httpService
            .get(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetchLocations), {})
            .pipe(finalize(() => this.loadingLocations = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (response: Array<IResource>) => this.locations = response,
                error: () => this.locations = new Array()
            });
    }

    private fetchPackageTypes(): void {
        this.loadingPackageTypes = true;
        this.resourceService.packageTypes()
            .pipe(finalize(() => this.loadingPackageTypes = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (response: Array<IResource>) => {
                    this.packageTypes = response;
                },
                error: () => this.packageTypes = new Array()
            });
    }

    private fetchModuleTypes(): void {
        this.loadingModuleTypes = true;
        this.resourceService.moduleTypes()
            .pipe(finalize(() => this.loadingModuleTypes = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (response: Array<IResource>) => {
                    this.moduleTypes = response;
                },
                error: () => this.moduleTypes = new Array()
            });
    }
    onOpenDocumentModal(content: TemplateRef<any>, record: PackageModuleModel) {
        this.packageModuleId = record.packageModuleId;
        this.buildForm();
        this.modalRef = this.modalService.open(content, {
            keyboard: false,
            centered: true,
            size: "lg",
            windowClass: "custom-modal effect-scale",
            backdrop: "static"
        });
    }

    onOpenViewDocumentModal(content: TemplateRef<any>, record: PackageModuleModel) {
        this.fetchDocuments(record.packageModuleId);
        this.modalRef = this.modalService.open(content, {
            backdrop: "static",
            keyboard: false,
            centered: true,
            windowClass: "document-view-modal custom-modal effect-scale"
        });
    }
    private fetchDocuments(packageModuleId) {
        this.httpService.post(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.fetchPackageDocuments), packageModuleId)
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe((response: Array<PackageDocument>) => {
                this.documents = response;
                if (response && response.length > 0) { this.showDocuments(response[0]); }
            });
    }
    showDocuments(document) {
        this.prepareDocument(document);
        const index = this.documents.findIndex((m => m.packageDocumentId === this.document.packageDocumentId) as any);
        if (index === 0 && this.documents.length === 1) {
            this.showPrevious = false;
            this.showNext = false;
        } else if (index === 0) {
            this.showPrevious = false;
            this.showNext = true;
        } else if (index === this.documents.length - 1) {
            this.showPrevious = true;
            this.showNext = false;
        } else {
            this.showPrevious = true;
            this.showNext = true;
        }
    }
    onPrevious() {
        let index = this.documents.findIndex((m => m.packageDocumentId === this.document.packageDocumentId) as any);
        index = index - 1;
        this.showPrevious = index !== 0;
        this.showNext = true;
        this.document = undefined;
        this.prepareDocument(this.documents[index]);
    }

    onNext() {
        let index = this.documents.findIndex((m => m.packageDocumentId === this.document.packageDocumentId) as any);
        index = index + 1;
        this.showNext = index !== this.documents.length - 1;
        this.showPrevious = true;
        this.document = undefined;
        this.prepareDocument(this.documents[index]);
    }
    private prepareDocument(document: PackageDocument) {
        this.loadingDocument = true;
        this.document = document;
        this.documentError = false;
        this.document.isImage = this.document.contentType.indexOf("pdf") < 0 && this.document.contentType.indexOf("video") < 0;
        this.document.isVideo = this.document.contentType.indexOf("video") >= 0;

        if (this.document.isImage || this.document.isVideo) {
            this.document.formedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${ApiResources.getURI(ApiResources.documents.base, ApiResources.documents.downloadFileGet)}?id=${this.document.packageDocumentId}&url=${this.document.documentUrl}&type=${'PackageDocuments'}`);
        }

        if (!this.document.isImage && !this.document.isVideo) {
            this.httpService
                .post(ApiResources.getURI(ApiResources.documents.base, ApiResources.documents.downloadFile), { ...document })
                .pipe(takeUntil(this.page.unSubscribe))
                .pipe(finalize(() => { this.loadingDocument = false; }))
                .subscribe(
                    (response: PackageDocument) => {
                        this.document.formedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`data:application/pdf;base64, ${response.base64}`);
                    }
                );

        } else {
            this.loadingDocument = false;
        }
    }
    private buildForm() {
        this.uploadDocumentForm = this.formBuilder.group({
            documentName: [null, [Validators.required, WhiteSpaceValidator.isValid]],
            description: [null]
        });

    }
    get form() { return this.uploadDocumentForm.controls }


    onFileSelect(files: Array<File>) {
        this.files = files;
    }

    onSubmitDocument() {
        this.submitted = true;
        if (!this.uploadDocumentForm.valid) {
            return;
        }
        this.submitting = true;
        this.errorMessage = undefined;
        const model = UtilHelper.clone(this.uploadDocumentForm.value);
        const formData = new FormData();
        formData.append("PackageModuleId", this.packageModuleId.toString());

        formData.append("UploadedBy", this.page.userAccount.accountId.toString());
        formData.append("DocumentName", model["documentName"]);
        formData.append("loginRoleId", this.page.userAccount.roleId.toString());
        formData.append("createdByName", this.page.userAccount.fullName);
        formData.append("locationId", this.page.userAccount.locationId.toString());

        const files = this.files;
        if (!files || !files.length) {
            this.submitting = false;
            this.notifyService.info("Please select at least one file");
            return;
        }
        const n = files.length;
        if (n > this.maxFiles) {
            files.splice(this.maxFiles, files.length);
        }
        files.forEach((file: File, index: number) => {
            formData.append(`File${index + 1}`, file, file.name);
        });
        this.httpService
            .postFile(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.uploadDocument), formData)
            .pipe(finalize(() => { this.submitted = false; this.submitting = false; }))
            .pipe(takeUntil(this.page.unSubscribe))
            .subscribe(
                () => {
                    this.files = new Array();
                    this.notifyService.successToast("Document uploaded successfully.");
                    this.onCloseModal();
                    this.fetchPackages();

                },
                (error: HttpErrorResponse) => {
                    this.errorMessage = UtilHelper.handleError(error);
                    this.notifyService.warning(this.errorMessage);
                }
            );
    }
    onDocumentLoad() {
        this.loadingDocument = false;
    }
    onDocumentError() {
        this.loadingDocument = false;
        this.documentError = true;
    }
    toggleVideo() {
        this.videoPlayer.nativeElement.play();
    }

    onProgress(progressData: ProgressData) {
        if (progressData.loaded === progressData.total) {
            this.loadingDocument = false;
        }
    }
    private fetchConsentForms(): void {
        this.loadingConsentForms = true;
        this.resourceService.consentForms()
            .pipe(finalize(() => this.loadingConsentForms = false), takeUntil(this.page.unSubscribe))
            .subscribe({
                next: (response: Array<IResource>) => {
                    this.consentFormIds = response
                    if (this.selectedPackage && this.selectedPackage.consentFormsIds) {
                        const consentFormIds = this.getIds(this.selectedPackage.consentFormsIds);
                        const consents = this.consentFormIds.filter(m => consentFormIds.includes(m.id));
                        this.consentForm.get("consentFormIds").setValue(consents);
                    } else {
                        this.consentForm.get("consentFormIds").setValue(null);
                    }
                },
                error: () => this.consentFormIds = []
            });
    }
    private getIds(value: string): Array<number> {
        if (value.indexOf(",") > -1)
            return value.split(",").map(m => parseInt(m));
        else
            return [parseInt(value)];
    }


    updateConsentForm() {
        this.updatedConsentIds = true
        if (this.consentForm.invalid) {
           
            return;
        }
        this.updattingConsentIds = true;
       
        const request = this.consentForm.getRawValue();
        request["consentFormIds"] = request.consentFormIds.map((m: IResource) => m.id).join(","),
        request["packageModuleId"] = this.selectedPackage.packageModuleId;
            console.log(request);
            this.httpService
                .post(ApiResources.getURI(ApiResources.packageModule.base, ApiResources.packageModule.updateConsentForm), request)
                .pipe(takeUntil(this.page.unSubscribe))
                .pipe(finalize(() => { this.updattingConsentIds = false; }))
                .subscribe(
                    (number: number) => {
                        if (number > 0) {
                            this.notifyService.successToast("Consent Form Updated");
                            this.onCloseModal();
                        }
                       
                    }
                );
        
        
    }

    private buildConsentFomr() {
        this.consentForm = this.formBuilder.group({
            consentFormIds: [null, [Validators.required]]
        });
    }


    get forms() {
        return this.consentForm.controls;
    }
}